// Continuum (k·p) wire models (phase SC3): the form of the Model step's "Continuum wire" tab and the parameters it
// sends (prayog_tb.continuum on the server). The wire runs along z (the experiments' Z), the cross-section is in x–y.

export type SocKind = "none" | "rashba" | "radial" | "dresselhaus" | "custom";
export interface ContinuumForm {
  seed: string;
  band: "conduction" | "valence";
  E0_meV: number;
  masses: number[];                 // m_x, m_y, m_z (m_e)
  soc_kind: SocKind;
  soc_strength: number;             // meV nm
  soc_axis: "x" | "y";              // Rashba: the spin-orbit field direction (across the wire)
  soc_custom: number[][];           // beta_ij (k_i sigma_j), meV nm
  // a pair split by 2Δ at k0 (Δ σ_x: Te's H4 / H5 at H) - needs two valleys (k0 and its time-reversal partner);
  // absent in forms saved before
  splitting_meV?: number;
  valleys?: 1 | 2;
  g_diag: number[];                 // kept with the model
  grid_nm: number;
  cross_section: "circle" | "rectangle";
  diameter_nm: number;
  width_nm: number[];
  fit?: Record<string, unknown> | null;
}

export function defaultContinuum(): ContinuumForm {
  return { seed: "wire", band: "conduction", E0_meV: 0, masses: [0.015, 0.015, 0.015], soc_kind: "rashba", soc_strength: 20,
    soc_axis: "x", soc_custom: [[0, 0, 0], [0, 0, 0], [0, 0, 0]], splitting_meV: 0, valleys: 1, g_diag: [50, 50, 50], grid_nm: 5,
    cross_section: "circle", diameter_nm: 60, width_nm: [60, 60], fit: null };
}

/** Presets: an InSb-like electron wire, and a starting point for a Te hole wire near H (replace by a fit): the top
 *  valence pair H4 / H5 split by 2Δ, with a k_z-linear term along the wire, in the two valleys H and H'. */
export function continuumPreset(kind: "insb" | "te", f: ContinuumForm): ContinuumForm {
  if (kind === "insb") return { ...f, seed: "insb_wire", band: "conduction", E0_meV: 0, masses: [0.015, 0.015, 0.015], soc_kind: "rashba",
    soc_strength: 20, soc_axis: "x", splitting_meV: 0, valleys: 1, g_diag: [50, 50, 50] };
  return { ...f, seed: "te_wire", band: "valence", E0_meV: -63, masses: [0.11, 0.11, 0.26], soc_kind: "custom",
    soc_custom: [[0, 0, 0], [0, 0, 0], [0, 0, 20]], splitting_meV: 63, valleys: 2, g_diag: [2, 2, 2], cross_section: "circle", diameter_nm: 65 };
}

export function socMatrix(f: ContinuumForm): number[][] {
  const z = () => [[0, 0, 0], [0, 0, 0], [0, 0, 0]];
  const b = z();
  const a = f.soc_strength;
  if (f.soc_kind === "rashba") b[2][f.soc_axis === "x" ? 0 : 1] = a;
  else if (f.soc_kind === "radial") { b[0][0] = a; b[1][1] = a; b[2][2] = a; }
  else if (f.soc_kind === "dresselhaus") { b[0][0] = a; b[1][1] = -a; }
  else if (f.soc_kind === "custom") return f.soc_custom.map((r) => [...r]);
  return b;
}

/** params.continuum of the negf.model job. */
export function continuumParams(f: ContinuumForm): Record<string, unknown> {
  const g = f.g_diag;
  const out: Record<string, unknown> = {
    seed: f.seed.trim() || "continuum", band: f.band, E0_meV: f.E0_meV, masses: f.masses, soc_meV_nm: socMatrix(f),
    splitting_meV: f.splitting_meV ?? 0, valleys: (f.splitting_meV ?? 0) > 0 ? 2 : (f.valleys ?? 1),
    g: [[g[0], 0, 0], [0, g[1], 0], [0, 0, g[2]]], grid_nm: f.grid_nm, shape: "wire", cross_section: f.cross_section,
    diameter_nm: f.diameter_nm, width_nm: f.width_nm,
  };
  if (f.fit) out.fit = f.fit;
  return out;
}

/** Grid points per slice (the server's count: points strictly inside the shape at spacing a). */
export function crossSectionPoints(f: ContinuumForm): number {
  const a = f.grid_nm;
  if (!(a > 0)) return 0;
  if (f.cross_section === "rectangle") {
    const n = (w: number) => Math.max(0, Math.round(w / a) - 1);
    return n(f.width_nm[0]) * n(f.width_nm[1]);
  }
  const R = f.diameter_nm / 2, n = Math.ceil(R / a) + 1;
  let c = 0;
  for (let j = -n; j <= n; j++) for (let i = -n; i <= n; i++) if ((i * a) ** 2 + (j * a) ** 2 < R * R - 1e-12) c++;
  return c;
}

export function continuumProblems(f: ContinuumForm): string[] {
  const out: string[] = [];
  if (!f.masses.every((m) => m > 0)) out.push("The masses must be positive.");
  if (!(f.grid_nm > 0)) out.push("The grid spacing must be positive.");
  if ((f.splitting_meV ?? 0) < 0) out.push("The pair splitting Δ must be 0 or above.");
  const n = crossSectionPoints(f);
  if (n === 0) out.push("The cross-section holds no grid point: a finer grid or a wider wire.");
  if (n > 20000) out.push(`${n} grid points per slice: at most 20000 - a coarser grid.`);
  return out;
}

/** Lowest subband of a hard-wall circle (Bessel zero 2.4048), meV: a check of the grid. */
export function circleSubband(f: ContinuumForm): number {
  const m = (f.masses[0] + f.masses[1]) / 2;
  return (38.0998212 * (2.404826 / (f.diameter_nm / 2)) ** 2) / m;
}

/** Fill the form from a negf.continuum_fit result. */
export function fromFit(f: ContinuumForm, p: Record<string, any>): ContinuumForm {
  const beta = (p.soc_meV_nm ?? [[0, 0, 0], [0, 0, 0], [0, 0, 0]]) as number[][];
  const d = [beta[0][0], beta[1][1], beta[2][2]];
  const radial = d.every((x) => Math.abs(x - d[0]) < 1e-9 * Math.max(1, Math.abs(d[0])));
  return { ...f, band: p.band, E0_meV: p.E0_meV, masses: p.masses, fit: p.fit ?? null, splitting_meV: p.splitting_meV ?? 0,
    valleys: p.valleys === 2 ? 2 : 1,
    soc_kind: d.every((x) => x === 0) ? "none" : radial ? "radial" : "custom", soc_strength: radial ? d[0] : f.soc_strength,
    soc_custom: beta };
}

export const K_POINTS: { value: string; label: string; k: number[] }[] = [
  { value: "G", label: "Γ (0, 0, 0)", k: [0, 0, 0] },
  { value: "H", label: "H (1/3, 1/3, 1/2) - Te's band edges", k: [1 / 3, 1 / 3, 0.5] },
  { value: "A", label: "A (0, 0, 1/2)", k: [0, 0, 0.5] },
  { value: "K", label: "K (1/3, 1/3, 0)", k: [1 / 3, 1 / 3, 0] },
];
